// No static Firebase imports to keep bundle lean and avoid SSR issues.
// All Firebase modules are loaded dynamically inside the functions below.
import type { FirebaseApp } from "firebase/app";
import type { Auth } from "firebase/auth";
import { getPublicEnv } from "../general/utils";

// Cache variables
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let initPromise: Promise<FirebaseApp> | null = null;

async function buildConfig() {
  const env = await getPublicEnv();
  const cfg = {
    apiKey: env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.NEXT_PUBLIC_FIREBASE_APP_ID,
    measurementId: env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
  };
  return cfg;
}

export async function getFirebaseApp(): Promise<FirebaseApp> {
  if (typeof window === "undefined") {
    throw new Error("Firebase App is not available server-side");
  }
  const config = await buildConfig();
  if (firebaseApp) return firebaseApp;
  if (!initPromise) {
    initPromise = (async () => {
      const appMod = await import("firebase/app");
      const { initializeApp, getApps } = appMod;

      if (!config.apiKey) {
        const msg =
          "Firebase initialization failed: NEXT_PUBLIC_FIREBASE_API_KEY is missing (not embedded at build and not available via runtime env endpoint).";
        throw new Error(msg);
      }
      firebaseApp =
        getApps().length === 0 ? initializeApp(config) : getApps()[0];
      return firebaseApp;
    })();
  }
  return initPromise;
}

export async function getFirebaseAuth(): Promise<Auth> {
  if (typeof window === "undefined") {
    throw new Error("Firebase Auth not available server-side");
  }
  const config = await buildConfig();
  // console.log("Getting Firebase Auth instance", config.apiKey);
  if (firebaseAuth !== null) return firebaseAuth;
  const app = await getFirebaseApp();
  const authMod = await import("firebase/auth");
  const { getAuth } = authMod;
  firebaseAuth = getAuth(app);
  return firebaseAuth;
}

// Optional: Lazy initialize Analytics when supported.
export async function initAnalytics() {
  if (typeof window === "undefined") return null;
  const app = await getFirebaseApp();
  try {
    const analyticsMod = await import("firebase/analytics");
    // Some environments require isSupported guard; check before calling getAnalytics
    const isSupported = (analyticsMod as any).isSupported;
    if (typeof isSupported === "function") {
      const supported = await isSupported();
      if (!supported) return null;
    }
    const { getAnalytics } = analyticsMod as any;
    return getAnalytics(app);
  } catch (e) {
    // Silently ignore analytics errors in favor of core auth/app
    if (process.env.NODE_ENV !== "production") {
      console.debug("Analytics init skipped:", e);
    }
    return null;
  }
}
